/* The run as a list of moments — what the bus page's mini map cannot say.
 *
 * Design leftover from e55ae0d, which rebuilt the page to "open with what the
 * bus is doing": the header gives the count (3 of 5 aboard) and the map gives
 * the shape, and neither says WHEN — that Amina was collected at 6:51 and
 * Baraka still has not been. Every one of those moments was already on the
 * roster row (the driver app stamps each status change); this reads them out
 * in order.
 *
 * Live run: the bus document's roster, so it moves as the driver marks
 * children. Parked: the last finished run's attendance record. Never both. */
import React from "react";

export type RunTimelineChild = {
  name: string;
  status: string;
  time: Date | null;
};

export interface RunTimelineProps {
  /** True while the run is in progress. */
  live: boolean;
  /** "morning" | "evening" | "school_trip" … or null. */
  leg: string | null;
  started: Date | null;
  finished: Date | null;
  km: number | null;
  children: RunTimelineChild[];
  now: number;
}

const INK = "#1B1633";
const INK2 = "#4B4566";
const MUTED = "#8E86A8";
const BORDER = "#E9E5F3";
const PANEL = "#F8F6FC";

function hm(d: Date | null): string {
  if (!d) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function legLabel(leg: string | null): string {
  switch (leg) {
    case "morning": return "Morning run";
    case "evening": case "evening_dropoff": return "Evening run";
    case "school_trip": return "School trip, outbound";
    case "school_trip_return": return "School trip, return";
    case "refuel": return "Refuel trip";
    default: return "Run";
  }
}

function minutesBetween(a: Date | null, b: Date | number | null): number | null {
  if (!a || b == null) return null;
  const end = typeof b === "number" ? b : b.getTime();
  return Math.max(0, Math.round((end - a.getTime()) / 60000));
}

/* What a status is CALLED on the timeline. Kept as words a parent would use —
   this list is read by a school admin who is not thinking in enum values. */
function verb(status: string, leg: string | null): { label: string; tone: "ok" | "warn" | "muted" } {
  switch (status) {
    case "picked_up":
      return { label: "Picked up", tone: "ok" };
    case "dropped_off":
    case "dropped":
      return { label: "Dropped off", tone: "ok" };
    case "absent":
      return { label: "Absent today", tone: "muted" };
    case "not_picked":
    case "pending":
      return leg && /evening|return/.test(leg)
        ? { label: "Not yet dropped", tone: "warn" }
        : { label: "Not yet collected", tone: "warn" };
    default:
      return { label: status.replace(/_/g, " "), tone: "muted" };
  }
}

const dotColor = { ok: "#16a34a", warn: "#f59e0b", muted: "#c4bfd6" } as const;

export default function RunTimeline({ live, leg, started, finished, km, children, now }: RunTimelineProps) {
  const stamped = children
    .filter((c) => c.time && c.status !== "not_picked" && c.status !== "pending")
    .sort((a, b) => (a.time!.getTime() - b.time!.getTime()));
  const waiting = children.filter((c) => c.status === "not_picked" || c.status === "pending");
  const absent = stamped.filter((c) => c.status === "absent").length;
  const elapsed = minutesBetween(started, finished ?? (live ? now : null));

  /* Nothing to show is a real state, not an empty box: a bus with no run
     recorded yet gets no timeline at all rather than a heading over silence. */
  if (!started && stamped.length === 0 && waiting.length === 0) return null;

  const title = live ? `${legLabel(leg)} · in progress` : `Last ${legLabel(leg).toLowerCase()}`;
  const sub = [
    started ? `started ${hm(started)}` : null,
    finished ? `finished ${hm(finished)}` : null,
    elapsed != null ? `${elapsed} min${live ? " so far" : ""}` : null,
    km != null && km > 0 ? `${km.toFixed(1)} km` : null,
  ].filter(Boolean).join(" · ");

  const row = (key: string, color: string, when: string, what: React.ReactNode, tone: "ok" | "warn" | "muted" = "ok") => (
    <li key={key} style={{ display: "grid", gridTemplateColumns: "58px 14px 1fr", alignItems: "center", gap: 10, minHeight: 30 }}>
      <span style={{ fontSize: 12, color: MUTED, fontFamily: "Geist Mono, ui-monospace, monospace", textAlign: "right" }}>{when}</span>
      <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: color, boxShadow: `0 0 0 3px ${PANEL}`, justifySelf: "center" }} />
      <span style={{ fontSize: 13.5, color: tone === "muted" ? MUTED : INK }}>{what}</span>
    </li>
  );

  return (
    <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "14px 16px", marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: INK2 }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: MUTED }}>{sub}</div>}
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, position: "relative" }}>
        {/* The spine. Sits under the dots; the dots' halo hides the join. */}
        <span aria-hidden style={{ position: "absolute", left: 58 + 10 + 6, top: 8, bottom: 8, width: 2, background: BORDER }} />
        {started && row("start", INK, hm(started), <b>Left the {leg && /evening|return/.test(leg) ? "school" : "depot"}</b>)}
        {stamped.map((c, i) => {
          const v = verb(c.status, leg);
          return row(`${c.name}-${i}`, dotColor[v.tone], hm(c.time), <><b>{v.label}</b> · {c.name}</>, v.tone);
        })}
        {finished && row("finish", INK, hm(finished), <b>Finished{km != null && km > 0 ? ` · ${km.toFixed(1)} km` : ""}</b>)}
      </ul>
      {waiting.length > 0 && (
        <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 12, background: PANEL, fontSize: 12.5, color: INK2 }}>
          <b style={{ color: live ? "#b45309" : INK2 }}>{live ? "Still to " : "Never "}{leg && /evening|return/.test(leg) ? "drop" : "collect"}{live ? "" : "ed"}:</b>{" "}
          {waiting.map((c) => c.name).join(", ")}
        </div>
      )}
      {absent > 0 && stamped.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: MUTED }}>{absent} marked absent</div>
      )}
    </div>
  );
}
